import * as React from 'react';
import { isEqual, uniqWith } from 'lodash';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Col,
    Form,
    FormInstance,
    message,
    Modal,
} from 'antd';
import { PickupAddress } from 'components/pickup/create-pickup/pickup.types';
import GenericHoc from 'components/common/generic-hoc';
import { HocOptions } from 'components/common/generic-hoc.types';
import { StylesProps, ThemeType } from 'theme/jss-types';
import {
    deleteAddress,
    fetchPickupAddressMultiple,
    getDefaultAddress,
    setDefaultAddressData,
    editAddress as editBusinessAddress,
} from '../../../../../network/pickup.api';
import { useDebounce } from 'hooks/use-debounce';
import { formRules } from 'library/constants';
import { NextOrPrev, Pagination } from 'library/Types';
import { formFields, addressFields } from '../../../create-modal.constants';
import { transformAddressFieldsForForm } from 'components/create-consignment/consignment-helper';
import { dockMappingFromApiArray } from 'components/address/DockVehicleMapping';
import Loader from 'components/common/Loader';
import CreateAddressForm from 'components/address/create-address';
import CreateAddressForm2 from 'components/address/create-address2';
import AddressCard from './AddressCard';
import AddressFormFields from './address-form-fields';
import Toolbar from './Toolbar';
import { addressFieldStyles } from './address-field.styles';

const normaliseAddress = (addr: PickupAddress): PickupAddress => ({
    ...addr,
    dockVehicleMapping: Array.isArray(addr?.dockVehicleMapping)
        ? dockMappingFromApiArray(addr.dockVehicleMapping)
        : addr?.dockVehicleMapping,
});

const { ConsignmentCategory } = formFields;

const mainStyles = (theme: ThemeType) => ({
    ...addressFieldStyles,
    addressCardSelected: {
        border: `1px solid ${theme.primaryColor}`,
        backgroundColor: '#E7F5FF',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    },
});

interface IProps extends StylesProps<ReturnType<typeof mainStyles>>, ReturnType<typeof mapStateToProps> {
    form: FormInstance;
    field: any;
    setCreatingAddress: (value: boolean) => void;
    params: any;
}

const AddressBook = (props: IProps) => {
    const { t, i18n } = useTranslation();
    const {
        classes,
        uiTheme,
        master,
        form,
        showAddressCode,
        defaultMultipleLimit,
        allowDefaultSourceAddress,
        phoneRegex,
        countryWisePhoneRegex,
        field,
        autofillAddressFieldsBasedOnAddressHierarchy,
        setCreatingAddress,
        params,
        hideAddressCardActions,
    } = props;

    const {
        subLayout, addressErrors, checkErrorOnAddressChange, disabledFields = {},
    } = params;
    const isAddressDisabled = !!disabledFields[field.key];

    const [defaultAddress, setDefaultAddress] = React.useState<Record<any, any>>({});
    const currentAddressType = field.key || 'srcAddress';

    const addressFieldLayout: string[] = subLayout?.[currentAddressType]?.length > 0
        ? subLayout[currentAddressType]
        : addressFields;
    const isCustomAddressFormMode = ['srcAddress', 'dstAddress'].includes(currentAddressType)
        && subLayout?.[currentAddressType]?.length > 0;

    // Clear all known fields first so a new address fully replaces the old one (setFieldsValue deep-merges).
    const withResetAddressKeys = (addr: PickupAddress): PickupAddress => {
        const resetValues: Record<string, any> = {};
        addressFieldLayout.forEach((fieldKey: string) => {
            resetValues[fieldKey] = undefined;
            // dockConfiguration is a composite layout slot; its actual form fields
            // (graceTime/dockCapacity/dockVehicleMapping) live as siblings, not nested under it.
            if (fieldKey === 'dockConfiguration') {
                resetValues.graceTime = undefined;
                resetValues.dockCapacity = undefined;
                resetValues.dockVehicleMapping = undefined;
            }
        });
        return normaliseAddress({ ...resetValues, ...addr });
    };

    const applyAddressSelection = (targetKey: string, addr: PickupAddress) => {
        form.setFieldsValue({ [targetKey]: null });
        form.setFieldsValue({ [targetKey]: withResetAddressKeys(addr) });
    };

    const editSavedAddress = React.useMemo((): PickupAddress | null => {
        if (!params.editData || isCustomAddressFormMode) return null;
        const prefixMap: Record<string, string> = {
            srcAddress: 'sender',
            dstAddress: 'destination',
        };
        const prefix = prefixMap[currentAddressType];
        if (!prefix) return null;
        const address = transformAddressFieldsForForm(params.editData, prefix);
        const hasAnyValue = Object.values(address).some((v) => v !== null && v !== undefined);
        return hasAnyValue ? address as unknown as PickupAddress : null;
    }, [params.editData, isCustomAddressFormMode, currentAddressType]);

    const [addressList, setAddressList] = React.useState<PickupAddress[]>([]);
    const [isSavedAddressSelected, setIsSavedAddressSelected] = React.useState<boolean>(false);
    const [loading, setLoading] = React.useState<boolean>(false);
    const selectedAddress: any = Form.useWatch(currentAddressType, form);
    const [searchString, setSearchString] = React.useState<string>('');
    const [editAddress, setEditAddress] = React.useState<PickupAddress | undefined>();
    const [showAddressFormModal, setShowAddressFormModal] = React.useState(false);
    const [refreshKey, setRefreshKey] = React.useState<number>(0);
    const [billToSame, setBillToSame] = React.useState<boolean>(false);
    const [isDefaultLoaded, setIsDefaultLoaded] = React.useState(false);
    const [showSavedAddressModal, setShowSavedAddressModal] = React.useState(false);
    const [modalSelectedAddress, setModalSelectedAddress] = React.useState<PickupAddress | null>(null);
    const [pagination, setPagination] = React.useState<Pagination>({
        nextOrPrev: 'first',
        currentPageNumber: 1,
        resultPerPage: 20,
        isNextPresent: false,
        lastUpdatedAt: null,
    });
    const dstAddressForBillDetails = Form.useWatch('dstAddress', form);
    const loadDefaultAddress = async () => {
        if (!allowDefaultSourceAddress) return;
        setLoading(true);
        const response = await getDefaultAddress();
        if (!response.isSuccess) {
            message.error(response.errorMessage);
        } else {
            setDefaultAddress(response.data);
        }
        setLoading(false);
    };

    const loadAddress = async (key: string) => {
        const formData = form.getFieldsValue();
        let defaultAdd: Record<any, any> = {};
        if (allowDefaultSourceAddress && !isDefaultLoaded && key === 'srcAddress') {
            const response = await getDefaultAddress();
            if (response.isSuccess) {
                defaultAdd = response?.data || {};
                setDefaultAddress(defaultAdd);
                setIsDefaultLoaded(true);
            }
        } else {
            defaultAdd = defaultAddress;
        }
        let showDefaultBy = '';
        if (defaultMultipleLimit) {
            showDefaultBy = key === 'srcAddress' ? 'src' : 'dst';
        }
        setLoading(true);
        const response = await fetchPickupAddressMultiple({
            showDefaultBy,
            searchString: searchString || '',
            parentCustomerId: master?.properties?.parent_address_book ? master?.Customer?.parent_id : undefined,
            pageNumber: pagination?.currentPageNumber,
            limit: 20,
        });
        if (!response.isSuccess) {
            message.error(response.errorMessage);
        }
        let data = response?.data?.page_data || [];
        // eslint-disable-next-line no-underscore-dangle
        if (key === 'srcAddress' && allowDefaultSourceAddress && defaultAdd?._id
            && searchString.length < 1 && !defaultMultipleLimit) {
            let add: PickupAddress | undefined;
            data = await data.filter((item: PickupAddress) => {
                // eslint-disable-next-line no-underscore-dangle
                if (item.id === defaultAdd._id) { add = item; return false; }
                return true;
            });
            if (add) await data.unshift(add);
        }
        if (formData?.previousSelectedAddress && formData?.previousSelectedAddress[key]) {
            data.unshift(formData?.previousSelectedAddress[key]);
        }
        const isNextPresent = response?.data?.is_next_present || false;
        setPagination({ ...pagination, isNextPresent });
        setAddressList(uniqWith(data, isEqual));
        setLoading(false);
    };

    const debounceTimeSrc = useDebounce(searchString);

    React.useEffect(() => {
        loadAddress(currentAddressType);
    }, [debounceTimeSrc, refreshKey]);

    React.useEffect(() => {
        if (editSavedAddress) {
            form.setFieldsValue({ [currentAddressType]: editSavedAddress });
            setIsSavedAddressSelected(true);
        }
    }, []);

    const isCurrentAddressSelected = (item: PickupAddress) => {
        if (isSavedAddressSelected) return false;
        return Boolean(selectedAddress && item.id === selectedAddress?.id);
    };


    const handleAddressChange = (e: any, key: string, item: PickupAddress) => {
        if (isAddressDisabled) return;
        setIsSavedAddressSelected(false);
        const isCurrentSelected = isCurrentAddressSelected(item);
        if (currentAddressType === 'billToDetails') setBillToSame(false);
        if (isCurrentSelected) {
            form.setFieldsValue({ [key]: null });
        } else {
            applyAddressSelection(key, item);
        }
        checkErrorOnAddressChange();
    };

    const saveDefaultAddress = async (address: PickupAddress, isMultipleDefault: boolean = false) => {
        setLoading(true);
        const customerCode = window.localStorage.getItem('userCode') || '';
        if (isMultipleDefault) {
            const response = await editBusinessAddress({ customerCode, ...address });
            if (!response.isSuccess) {
                message.error(response.errorMessage);
            } else {
                loadAddress(currentAddressType);
            }
        } else {
            const id = address.id;
            const response = await setDefaultAddressData({ businessAddressId: id });
            if (!response.isSuccess) {
                message.error(response.errorMessage);
            } else {
                loadDefaultAddress();
                setLoading(true);
                if (currentAddressType === 'srcAddress') {
                    let data = addressList || [];
                    // eslint-disable-next-line no-underscore-dangle
                    if (allowDefaultSourceAddress && id && searchString.length < 1) {
                        let add: PickupAddress | undefined;
                        data = data.filter((item: PickupAddress) => {
                            if (item.id === id) { add = item; return false; }
                            return true;
                        });
                        if (add) data.unshift(add);
                        setAddressList(data);
                    }
                }
            }
        }
        setLoading(false);
    };

    const handleDelete = async (e: any, item: PickupAddress) => {
        const customerCode = window.localStorage.getItem('userCode') || '';
        const result = await deleteAddress({ id: item.id, addressCode: item.addressCode, customerCode });
        if (result.isSuccess) {
            message.success(t('address_deleted_successfully'));
            setRefreshKey((k) => k + 1);
        } else {
            message.error(result.errorMessage);
        }
    };

    const handlePagination = (nextOrPrev: NextOrPrev) => {
        const { currentPageNumber } = pagination;
        let newPageNumber = currentPageNumber;
        switch (nextOrPrev) {
            case 'first': newPageNumber = 1; break;
            case 'next': newPageNumber = currentPageNumber + 1; break;
            case 'prev': newPageNumber = currentPageNumber - 1; break;
            default: break;
        }
        setPagination({ ...pagination, nextOrPrev, currentPageNumber: newPageNumber });
        setRefreshKey((k) => k + 1);
    };

    const formRuleRequired = {
        ...formRules.required,
        message: i18n.exists('required') ? t('required') : 'Required',
    };

    const sharedCardProps = {
        addressKey: currentAddressType,
        showAddressCode: showAddressCode || false,
        primaryColor: uiTheme.primaryColor,
        allowDefaultSourceAddress: allowDefaultSourceAddress || false,
        defaultMultipleLimit,
        defaultAddress,
        currentAddressType,
        onSaveDefault: saveDefaultAddress,
        onConfirmDelete: handleDelete,
        isReadOnly: hideAddressCardActions,
    };

    const renderAddressList = (key: string) => {
        if (loading) return <Loader zIndex={10} />;
        return (
            <Form.Item
                name={key}
                className={classes.formItem}
                rules={['srcAddress', 'dstAddress'].indexOf(key) > -1 ? [formRuleRequired] : []}
            >
                <div className={classes.addressList}>
                    {addressList?.map((item: PickupAddress) => (
                        <AddressCard
                            key={item.id}
                            item={item}
                            isSelected={isCurrentAddressSelected(item)}
                            onSelect={(e) => handleAddressChange(e, key, item)}
                            onEdit={() => {
                                setEditAddress(item);
                                setShowAddressFormModal(true);
                                setCreatingAddress(true);
                            }}
                            {...sharedCardProps}
                        />
                    ))}
                </div>
            </Form.Item>
        );
    };

    const closeAddressFormModal = () => {
        setShowAddressFormModal(false);
        setEditAddress(undefined);
        setCreatingAddress(false);
    };

    const handleSavedAddressSelect = () => {
        if (isAddressDisabled || !editSavedAddress) return;
        if (isSavedAddressSelected) {
            form.setFieldsValue({ [currentAddressType]: null });
            setIsSavedAddressSelected(false);
        } else {
            form.setFieldsValue({ [currentAddressType]: editSavedAddress });
            setIsSavedAddressSelected(true);
        }
        checkErrorOnAddressChange();
    };

    const renderSavedAddressCard = () => {
        if (!editSavedAddress) return null;
        return (
            <div className={classes.savedAddressSection}>
                <div className={classes.savedAddressLabel}>{t('current_address')}</div>
                <AddressCard
                    item={editSavedAddress}
                    isSelected={isSavedAddressSelected}
                    onSelect={handleSavedAddressSelect}
                    onEdit={() => {}}
                    onConfirmDelete={() => {}}
                    {...sharedCardProps}
                    isReadOnly
                />
            </div>
        );
    };

    const renderAddressForm = (key: string) => {
        const isInternationalAddress = form.getFieldValue(ConsignmentCategory.key) === 'international'
            && currentAddressType !== 'srcAddress';

        // eslint-disable-next-line no-underscore-dangle
        const showSelectDefault = Boolean(allowDefaultSourceAddress && !defaultMultipleLimit && defaultAddress?._id);
        const onSelectDefault = () => {
            // eslint-disable-next-line no-underscore-dangle
            const item = addressList?.find((add) => add.id === defaultAddress?._id);
            if (item) applyAddressSelection(key, item);
        };

        const openAddressFormModal = (addressToEdit?: PickupAddress) => {
            setEditAddress(addressToEdit);
            setShowAddressFormModal(true);
            setCreatingAddress(true);
        };

        const handleAddressFormUpdate = (apiBody: any) => {
            if (apiBody && editAddress) {
                if (isCustomAddressFormMode) {
                    if (modalSelectedAddress?.id === editAddress.id) {
                        setModalSelectedAddress({ ...modalSelectedAddress, ...apiBody });
                    }
                } else if (selectedAddress?.id === editAddress.id) {
                    applyAddressSelection(currentAddressType, { ...selectedAddress, ...apiBody });
                    checkErrorOnAddressChange();
                }
            }
            closeAddressFormModal();
            setRefreshKey((k) => k + 1);
        };

        const addressFormModal = (
            <Modal
                title={editAddress ? t('edit_address') : t('add_address')}
                open={showAddressFormModal}
                onCancel={closeAddressFormModal}
                footer={null}
                width={700}
                destroyOnClose
            >
                {autofillAddressFieldsBasedOnAddressHierarchy
                    ? (
                        <CreateAddressForm2
                            editData={editAddress}
                            page="Address"
                            onClose={closeAddressFormModal}
                            phoneRegex={phoneRegex}
                            isInternationalAddress={isInternationalAddress}
                            countryWisePhoneRegex={countryWisePhoneRegex}
                            showBackButton
                            showSaveButtonAtBottom
                            updateFormData={handleAddressFormUpdate}
                        />
                    ) : (
                        <CreateAddressForm
                            editData={editAddress}
                            page="Address"
                            onClose={closeAddressFormModal}
                            phoneRegex={phoneRegex}
                            isInternationalAddress={isInternationalAddress}
                            countryWisePhoneRegex={countryWisePhoneRegex}
                            showBackButton
                            showSaveButtonAtBottom
                            updateFormData={handleAddressFormUpdate}
                        />
                    )}
            </Modal>
        );

        if (isCustomAddressFormMode) {
            const openSavedModal = () => {
                setModalSelectedAddress(null);
                setShowSavedAddressModal(true);
            };

            const closeSavedModal = () => {
                setShowSavedAddressModal(false);
                setModalSelectedAddress(null);
            };

            const submitSavedModal = () => {
                if (modalSelectedAddress) {
                    applyAddressSelection(currentAddressType, modalSelectedAddress);
                    checkErrorOnAddressChange();
                }
                closeSavedModal();
            };

            return (
                <>
                    {addressFormModal}
                    <div>
                        <Button
                            type="link"
                            className={classes.chooseSavedAddressLink}
                            onClick={openSavedModal}
                            disabled={isAddressDisabled}
                        >
                            {t('choose_saved_address')}
                        </Button>
                        <AddressFormFields
                            addressKey={currentAddressType}
                            editData={params.editData}
                            addressFieldLayout={addressFieldLayout}
                            {...props}
                        />
                        <Modal
                            title={t('choose_saved_address')}
                            open={showSavedAddressModal}
                            onCancel={closeSavedModal}
                            width={800}
                            footer={[
                                <Button key="cancel" onClick={closeSavedModal}>
                                    {t('cancel')}
                                </Button>,
                                <Button
                                    key="submit"
                                    type="primary"
                                    onClick={submitSavedModal}
                                    disabled={!modalSelectedAddress}
                                >
                                    {t('submit')}
                                </Button>,
                            ]}
                        >
                            <Toolbar
                                currentAddressType={currentAddressType}
                                billToSame={billToSame}
                                onBillToSameChange={(checked: boolean) => {
                                    setBillToSame(checked);
                                    if (checked) form.setFieldsValue({ billToDetails: dstAddressForBillDetails });
                                }}
                                onSearch={setSearchString}
                                showSelectDefault={showSelectDefault}
                                onSelectDefault={onSelectDefault}
                                pagination={pagination}
                                onPaginationChange={handlePagination}
                                onAddNew={() => openAddressFormModal(undefined)}
                                disabled={isAddressDisabled}
                            />
                            {addressErrors[`${currentAddressType}Main`] ? (
                                <div className={classes.errorMessage}>
                                    {addressErrors[`${currentAddressType}Main`]}
                                </div>
                            ) : null}
                            {loading ? <Loader zIndex={10} /> : (
                                <div className={classes.addressList}>
                                    {addressList?.map((item: PickupAddress) => (
                                        <AddressCard
                                            key={item.id}
                                            item={item}
                                            isSelected={modalSelectedAddress?.id === item.id}
                                            onSelect={() => {
                                                setModalSelectedAddress(
                                                    modalSelectedAddress?.id === item.id ? null : item,
                                                );
                                            }}
                                            onEdit={() => openAddressFormModal(item)}
                                            {...sharedCardProps}
                                        />
                                    ))}
                                </div>
                            )}
                        </Modal>
                    </div>
                </>
            );
        }

        return (
            <>
                {addressFormModal}
                <div>
                    {renderSavedAddressCard()}
                    <Toolbar
                        currentAddressType={currentAddressType}
                        billToSame={billToSame}
                        onBillToSameChange={(checked: boolean) => {
                            setBillToSame(checked);
                            if (checked) form.setFieldsValue({ billToDetails: dstAddressForBillDetails });
                        }}
                        onSearch={setSearchString}
                        showSelectDefault={showSelectDefault}
                        onSelectDefault={onSelectDefault}
                        pagination={pagination}
                        onPaginationChange={handlePagination}
                        onAddNew={() => openAddressFormModal(undefined)}
                    />
                    {addressErrors[`${currentAddressType}Main`] ? (
                        <div className={classes.errorMessage}>
                            {addressErrors[`${currentAddressType}Main`]}
                        </div>
                    ) : null}
                    {renderAddressList(key)}
                </div>
            </>
        );
    };

    return (
        <Col span={24}>
            <div className={classes.addressWrapper}>
                {renderAddressForm(currentAddressType)}
            </div>
        </Col>
    );
};

const mapStateToProps = (state: any) => {
    const { master, uiTheme } = state;
    const { config, parts_to_show } = master;
    const customerPortalConfig = config?.customer_portal_config;
    const showAddressCode = config?.customer_portal_config?.allow_address_code_in_addresses;
    const disableDefaultAddress = !customerPortalConfig?.disable_default_address;
    const defaultAddressLimit = customerPortalConfig?.default_addresses_limit;
    const allowDefaultSourceAddress = disableDefaultAddress && defaultAddressLimit;
    const defaultMultipleLimit = config?.customer_portal_config?.default_addresses_limit;
    const phoneRegex = parts_to_show?.remove_regex ? null : config?.phone_regex;
    const countryWisePhoneRegex = config?.country_wise_phone_regex || [];
    const autofillAddressFieldsBasedOnAddressHierarchy = parts_to_show?.autofill_address_fields_based_on_address_hierarchy;
    const hideAddressCardActions = config?.customer_portal_order_creation_config?.hide_address_card_actions || false;
    return {
        uiTheme,
        master,
        showAddressCode,
        allowDefaultSourceAddress,
        defaultMultipleLimit,
        phoneRegex,
        countryWisePhoneRegex,
        autofillAddressFieldsBasedOnAddressHierarchy,
        hideAddressCardActions,
    };
};

const hocConfig: HocOptions = {
    connectRedux: {
        useRedux: true,
        mapStateToProps,
    },
    connectJss: {
        useJss: true,
        styleSheet: mainStyles,
    },
};

export default GenericHoc(hocConfig)(AddressBook);
